import { WOONPLAATS_VOORVOEGSEL } from "../api/insights";
import type { FilterKeuze, Leeftijdseenheid } from "./DataInsightFilters";

/**
 * De toegepaste filters in leesbare regels, voor de kop van een export.
 *
 * <p><b>Waarom dit bestaat:</b> een los csv- of Excel-bestand is drie maanden later niet meer te
 * plaatsen. "Aantal dieren binnengekomen: 1.248" is een ander getal met en zonder leeftijdsgrens,
 * en zonder deze regels wordt er gerekend met cijfers waarvan niemand meer weet waar ze over
 * gingen. Het exporteren van cijfers zonder hun afbakening is erger dan niet exporteren: het
 * bestand ziet er even betrouwbaar uit.
 *
 * <p>Alleen wat er echt staat. Een regel "Diersoort: alle" is geen informatie maar ruis, en tussen
 * tien van die regels valt de ene grens die er wél staat niet meer op.
 */
export function filterOmschrijving(k: FilterKeuze, t: (sleutel: string) => string): string[] {
  const uit: string[] = [];

  const van = k.vanDatum ?? (k.vanJaar == null ? null : String(k.vanJaar));
  const tot = k.totDatum ?? (k.totJaar == null ? null : String(k.totJaar));
  if (van || tot) {
    // Eén kant ingevuld hoort ook te worden opgeschreven: "vanaf 2024" is een echte afbakening.
    uit.push(`${t("dataInsight.periode")}: ${van ?? "…"} ${t("dataInsight.tot")} ${tot ?? "…"}`
      + ` (${t(eenheidSleutel(k.eenheid))})`);
  }
  if (k.diersoorten.length > 0) {
    uit.push(`${t("dataInsight.diersoort")}: ${k.diersoorten.join(", ")}`);
  }
  if (k.gemeente) {
    // Gemeente of woonplaats: welke van de twee het is bepaalt het antwoord, dus dat hoort erbij.
    // "Utrecht" als gemeente is een stuk groter dan "Utrecht" als woonplaats.
    uit.push(k.gemeente.startsWith(WOONPLAATS_VOORVOEGSEL)
      ? `${t("dataInsight.woonplaatsen")}: ${k.gemeente.slice(WOONPLAATS_VOORVOEGSEL.length)}`
      : `${t("dataInsight.gemeenten")}: ${k.gemeente}`);
  }
  const grenzen: string[] = [];
  if (k.ouderDan) {
    grenzen.push(`${t("dataInsight.ouderDan")} ${k.ouderDan} ${t(leeftijd(k.ouderDanEenheid))}`);
  }
  if (k.jongerDan) {
    grenzen.push(`${t("dataInsight.jongerDan")} ${k.jongerDan} ${t(leeftijd(k.jongerDanEenheid))}`);
  }
  if (grenzen.length > 0) {
    // De uitzondering hoort bij de grens en niet in een voetnoot: dieren zonder geboortedatum
    // vallen hierbuiten, en dat verklaart een aantal dat lager is dan verwacht.
    uit.push(`${t("dataInsight.leeftijd")}: ${grenzen.join(" · ")}`
      + ` — ${t("dataInsight.leeftijdOnbekend")}`);
  }
  if (k.vestigingen.length > 0) {
    uit.push(`${t("dataInsight.welkeVestigingen")}: ${k.vestigingen.join(", ")}`);
  }
  if (k.cumulatief) {
    // Een weergavekeuze, maar wel een die de getallen in het bestand verandert: elk cijfer is dan
    // een lopend totaal en niet de periode zelf.
    uit.push(t("dataInsight.cumulatief"));
  }
  return uit;
}

function eenheidSleutel(eenheid: FilterKeuze["eenheid"]): string {
  return {
    DAG: "dataInsight.perDag",
    WEEK: "dataInsight.perWeek",
    MAAND: "dataInsight.perMaand",
    KWARTAAL: "dataInsight.perKwartaal",
    JAAR: "dataInsight.perJaar",
  }[eenheid];
}

function leeftijd(eenheid: Leeftijdseenheid): string {
  return {
    WEKEN: "dataInsight.leeftijdWeken",
    MAANDEN: "dataInsight.leeftijdMaanden",
    JAREN: "dataInsight.leeftijdJaren",
  }[eenheid];
}
